import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import DurationSlider from "../../../Inputs/DurationSlider";
import Weekday, {
    getLabeledWeekdays,
    getShiftedWeekday,
    getShortLabeledWeekdayForWeekdayIndex,
} from "../../../../utils/weekday";
import TimeSpan, { getTimeSpanDecimalHours } from "../../../../utils/time-span";
import { ScheduleEntryTimes } from "../../../../interfaces/robot/scheduling/schedule-entry";
import useInputEffect from "../../../../hooks/useInputEffect";
import TimePicker from "../../../Inputs/TimePicker";

const useStyles = makeStyles()((theme) => ({
    timeUntilBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100%",
    },
    weekdaysToggleButtonSelected: {
        color: `${theme.palette.primary.main}CC !important`,
    },
}));

const getScheduledTimes = (
    weekdays: { weekdayFrom: Weekday; weekdayTo: Weekday }[],
    timeFrom: TimeSpan,
    timeTo: TimeSpan
) => {
    return weekdays.map((w) => {
        const fromMinutes = getTimeSpanDecimalHours(timeFrom) * 60;
        const toMinutes = getTimeSpanDecimalHours(timeTo) * 60;

        const crossesDay = toMinutes <= fromMinutes || (toMinutes - fromMinutes + 24 * 60) % (24 * 60) === 0;

        const weekdayTo = crossesDay ? getShiftedWeekday(w.weekdayFrom, 1) : w.weekdayFrom;

        return {
            ...w,
            weekdayTo,
            timeFrom: timeFrom,
            timeTo: timeTo,
        };
    });
};

const getDurationMinutes = (
    timeFrom: TimeSpan,
    timeTo: TimeSpan,
    weekdaysFromTo?: { weekdayFrom: Weekday; weekdayTo: Weekday }
) => {
    const fromMinutes = timeFrom.hours * 60 + timeFrom.minutes;
    const toMinutes = timeTo.hours * 60 + timeTo.minutes;

    let diff = toMinutes - fromMinutes;
    if (diff <= 0 || (weekdaysFromTo && weekdaysFromTo.weekdayFrom !== weekdaysFromTo.weekdayTo)) {
        diff += 24 * 60;
    }

    return diff;
};

const SchedulerEntryTimes: React.FC<{
    timeFrom: TimeSpan;
    timeTo: TimeSpan;
    weekdaysFromTo?: { weekdayFrom: Weekday; weekdayTo: Weekday };
    recurringWeekdays: Weekday[];
    onScheduledTimesChange: (scheduledTimes: ScheduleEntryTimes[], noDaysSelected: boolean) => void;
}> = (props) => {
    const { classes } = useStyles();

    const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

    const { t } = useTranslation();

    const initialDuration = getDurationMinutes(props.timeFrom, props.timeTo, props.weekdaysFromTo);

    const [duration, setDuration] = useState(initialDuration);

    const {
        value: recurringWeekdays,
        valueChangeHandler: recurringWeekdaysChangeHandler,
        setValue: setRecurringWeekdays,
    } = useInputEffect<Weekday[], any, Weekday[]>(
        props.recurringWeekdays ? props.recurringWeekdays : [],
        (newRecurringWeekdays) => {
            if (newRecurringWeekdays != null) {
                updateScheduledTimes(timeFrom, duration, newRecurringWeekdays, props.weekdaysFromTo);
            }
        },
        (_, newWeekdays) => {
            if (newWeekdays != null) setRecurringWeekdays(newWeekdays);
        }
    );

    const {
        value: timeFrom,
        valueChangeHandler: timeFromChangeHandler,
        setValue: setTimeFrom,
    } = useInputEffect<TimeSpan, TimeSpan, void>(
        props.timeFrom,
        (newTimeFrom) => {
            updateScheduledTimes(newTimeFrom, duration, recurringWeekdays, props.weekdaysFromTo);
        },
        (event, _) => {
            setTimeFrom(event);
        }
    );

    const labeledWeekdays = getLabeledWeekdays();

    const updateScheduledTimes = useCallback(
        (
            startTime: TimeSpan,
            dur: number,
            recurring: Weekday[],
            weekdaysFromTo?: { weekdayFrom: Weekday; weekdayTo: Weekday }
        ) => {
            let weekdays: { weekdayFrom: Weekday; weekdayTo: Weekday }[] = [];

            if (recurring && recurring.length > 0) {
                weekdays = recurring.map((w) => ({ weekdayFrom: w, weekdayTo: w }));
            } else if (weekdaysFromTo) {
                weekdays = [
                    {
                        weekdayFrom: weekdaysFromTo.weekdayFrom,
                        weekdayTo: weekdaysFromTo.weekdayTo,
                    },
                ];
            } else {
                return;
            }

            const computedTimeTo = computeTimeTo(startTime, dur);
            const noDaysSelected = recurringWeekdays.length === 0;
            props.onScheduledTimesChange(getScheduledTimes(weekdays, startTime, computedTimeTo), noDaysSelected);
        },
        [props, recurringWeekdays?.length]
    );

    useEffect(() => {
        updateScheduledTimes(timeFrom, duration, recurringWeekdays, props.weekdaysFromTo);
    }, [timeFrom, duration, recurringWeekdays, props.weekdaysFromTo, updateScheduledTimes]);

    const computeTimeTo = (start: TimeSpan, durationMinutes: number): TimeSpan => {
        const totalMinutes = start.hours * 60 + start.minutes + durationMinutes;
        const wrapped = totalMinutes % (24 * 60);
        return { hours: Math.floor(wrapped / 60), minutes: wrapped % 60 };
    };

    const durationChangeHandler = (newValue: number) => {
        setDuration(newValue);
    };

    return (
        <React.Fragment>
            <Stack width="100%" gap={2} pt={2}>
                <Grid container justifyContent="space-between" gap={3}>
                    {/* start time */}
                    <Grid item sm={12} lg={3}>
                        <Typography variant="subtitle2">{t("scheduling-start-time")}</Typography>
                        <TimePicker value={timeFrom} onValueChange={timeFromChangeHandler} maxTime={{ hours: 23, minutes: 45 }} />
                    </Grid>
                    {/* duration */}
                    <DurationSlider value={duration} timeFrom={timeFrom} onSliderChange={durationChangeHandler} />
                </Grid>
                {/* weekdays */}
                <Grid container>
                    <Grid item xs={12} id="recurring-day-selection">
                        <Typography variant="subtitle2">{t("scheduling-repeat-on-days")}</Typography>
                        <ToggleButtonGroup
                            value={recurringWeekdays}
                            onChange={recurringWeekdaysChangeHandler}
                            size="small"
                            sx={(theme) => ({ width: "100%", height: theme.spacing(5) })}
                        >
                            {labeledWeekdays.map((lw) => (
                                <ToggleButton
                                    key={lw.value}
                                    value={lw.value}
                                    classes={{ selected: classes.weekdaysToggleButtonSelected }}
                                    sx={{
                                        width: `${100 / labeledWeekdays.length}%`,
                                        fontSize: matchesSmUp ? "0.87rem" : "small",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {getShortLabeledWeekdayForWeekdayIndex(lw.index)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid item display="flex" alignItems="center">
                        {recurringWeekdays.length === 0 && <FormHelperText error>{t("select-days-to-continue")}</FormHelperText>}
                    </Grid>
                </Grid>
            </Stack>
        </React.Fragment>
    );
};

export default SchedulerEntryTimes;